import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import React, { ReactElement, ReactNode } from 'react';

import { WeeeIcon } from '@/components/common/Icon';
import ProductCard from '@/components/common/ProductCard';
import Skeleton from '@/components/common/Skeleton';
import { AddToCartReferType, CategoryMod, ClickType } from '@/constants/track';
import elementInView from '@/hooks/elementInView';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { ContextParam } from '@/types/track';
import { VendorCategory } from '@/types/vendor';
import { getDeliveryDate } from '@/utils/common/porder';

import styles from './index.module.scss';

type CProps = {
  className?: string;
  title: string;
  cardId: number;
  children: ReactNode | ReactElement;
  position: number;
  actionT2?: () => void;
};

export const CardLayer: React.FC<CProps> = props => {
  const { t, lang } = useTranslation('common');
  return (
    <div id={`${props?.cardId}`} className={classNames(styles.sectionLayer, props.className)}>
      <a
        className={styles.sectionTop}
        onClick={e => {
          e.stopPropagation();
          props?.actionT2?.();
        }}
        href={`/${lang}/vendor/${props?.cardId}`}>
        <span className={styles.title}>{props.title}</span>
        <span className={styles.moreLink}>
          <WeeeIcon type="iconArrowRight" />
        </span>
      </a>
      <div className={styles.sectionContent}>{props.children}</div>
    </div>
  );
};

type VProps = {
  className?: string;
  dataSource: VendorCategory[];
  queryParams?: ContextParam;
};

const VendorCard: React.FC<VProps> = props => {
  const { className, dataSource, queryParams } = props;
  const { customButtonClick } = useClickActionT2();

  const { t, lang } = useTranslation('common');

  const exploreTrack = (v, i) => {
    const param = {
      mod_nm: CategoryMod.itemList,
      sec_nm: `${v}`,
      sec_pos: i,
      co: {
        target_nm: 'explore_more',
        click_type: ClickType.normal
      }
    };
    customButtonClick({
      ...param
    });
  };

  return (
    <>
      {dataSource.map((vender, v_index) => {
        return (
          <CardLayer
            key={`vender-${v_index}-${vender?.vender_name}`}
            className={className}
            title={vender?.vender_name}
            position={v_index}
            cardId={vender?.vender_id}
            actionT2={() => {
              exploreTrack(vender?.vender_id, v_index);
            }}>
            <div className={styles.vendorSwiper}>
              {vender?.products?.slice(0, 10).map((item, index) => {
                return (
                  <div className={styles.vendorItem} key={`vendor-item-${index}`}>
                    <ProductCard
                      className={styles.vendorCard}
                      data={item}
                      showLeft={false}
                      addCartParams={{
                        referType: AddToCartReferType.seller,
                        referValue: `${item?.vender_id}`,
                        deliveryDate: getDeliveryDate()
                      }}
                      positionInfoT2={{
                        modSecPos: {
                          mod_nm: CategoryMod.itemList,
                          mod_pos: 2,
                          sec_nm: `${vender?.vender_id}`,
                          sec_pos: v_index
                        },
                        prodPos: index,
                        context: {
                          ...queryParams
                        }
                      }}
                      showTags={false}
                      skeletonFactor={elementInView(item?.id)}
                      skeleton={<Skeleton.Card />}
                    />
                  </div>
                );
              })}
              {vender?.products?.length > 10 && (
                <Link prefetch={false} href={`/mkpl/vendor/${vender?.vender_id}`}>
                  <a
                    onClick={e => {
                      e.stopPropagation();
                      exploreTrack(vender?.vender_id, v_index);
                    }}
                    className={styles.viewMore}
                    slot="wrapper-end">
                    <span className={styles.moreIcon}>
                      <WeeeIcon type="iconArrowRight" />
                    </span>
                    <span className={styles.checkMore}>{t('themes_check_more')}</span>
                  </a>
                </Link>
              )}
            </div>
          </CardLayer>
        );
      })}
    </>
  );
};

export default VendorCard;
